import { useReactiveVar } from '@apollo/client'
import { ViewItemReorderInput } from '@deltek/specification-client/core/Models/projectProductFamily'
import { ViewItemReorderPTPInput } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { resetOnFocusProperty } from 'api/graphQL/reactiveVars/property'
import {
    MoveAction,
    moveUpAndDown,
    promoteDemoteTemplateData,
    reactiveWorkspaceActions,
    setMoveUpAndDownTrigger,
    setPromoteDemoteContent,
    setPromoteDemoteTriggered,
    setReactiveWorkspaceActions,
    setWorkspaceTemplateData,
    templateData,
    templateFlags,
} from 'api/graphQL/reactiveVars/workspaceActions'
import {
    isContentTeam,
    isOmWorkspaceUser,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { workspaceToggles } from 'api/graphQL/reactiveVars/workspaceToggles'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import {
    isContentAllowedToBeDemoted,
    isContentAllowedToBePromoted,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/addUtils'
import {
    ParagraphAction,
    PromoteDemoteRequirements,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import {
    getMoveDownIndex,
    getMoveUpIndex,
    moveItemOrGroup,
    setPlacementForBulletsForDTV,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import {
    areEq,
    insertPillTextUOM,
} from 'pages/ProjectsPage/WorkspacePage/components/PropertyEditor/propertyEditorUtils'
import {
    ActionButtonVariant,
    PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys,
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as TextVariants,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React from 'react'
import {
    ActionButton,
    ActionButtons,
    ExtendedSvgIcon,
    actionButtonsHelper,
} from '../ActionBar'

const {
    MoveDown,
    MoveUp,
    Demote,
    Promote,
    InsertPill,
    InsertText,
    InsertUOM,
    SpecifierNote,
} = ActionButtonVariant

export const useEditableContentActionBarStyles = makeStyles((theme: Theme) => ({
    roundContainerActionBar: {
        display: 'flex',
        position: 'relative',
        top: '20px',
        alignItems: 'center',
        backgroundColor: '#314959',
        borderRadius: '20px',
        height: pxToRem(38),
        width: 'auto',
        padding: theme.spacing(0, '15px'),
        opacity: 1,
        transition: 'transform 200ms ease-in-out, opacity 300ms ease-in-out',
    },
    actionButtonGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(5),
    },
    expandActionBar: {
        transform: 'translateY(-20px)',
        opacity: 1,
    },
    collapseActionBar: {
        transition: '100ms',
        transform: 'translateY(0)',
        opacity: 0,
    },
}))

interface EditableContentActionBarProps {
    handleFamilyReorderTemplates?: (
        reOrderFamilyContentInput: Partial<ViewItemReorderInput>
    ) => void
    handleProductTypeReorderTemplates?: (
        reOrderPTContentInput: Partial<ViewItemReorderPTPInput>
    ) => void
    projectNuggetId?: string
    isInProject: boolean
    contentTeamFromStudioPage: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
}

export const EditableContentActionBar: React.FC<Partial<
    EditableContentActionBarProps
>> = ({
    handleFamilyReorderTemplates,
    handleProductTypeReorderTemplates,
    projectNuggetId,
    isInProject,
    contentTeamFromStudioPage,
    selectedTopic,
}) => {
    const {
        actionButtonGroup,
        roundContainerActionBar,
        collapseActionBar,
        expandActionBar,
    } = useEditableContentActionBarStyles()
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const {
        projectProductFamilyId,
        contentItemNodes,
        dtv,
        isDraftMode,
    } = workspaceContents()
    const { areSharedPropertiesVisible: areSharedVisible } = useReactiveVar(
        workspaceToggles
    )
    const { workspaceTemplateData } = useReactiveVar(templateData)
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const { promoteDemoteWorkspaceTemplateData } = useReactiveVar(
        promoteDemoteTemplateData
    )
    const { isMoveUpAndDownTriggered, action, isShortcutKey } = useReactiveVar(
        moveUpAndDown
    )
    const { isTemplateDataLastItem } = useReactiveVar(templateFlags)

    const {
        showSMMActionButtons,
        isAddSpecifierNote,
        isInsertPill,
        isInsertText,
        isInsertUOM,
        insertUomPill,
        insertPillRequirements,
        addNoteSpecifier,
        addNoteSpecifierRequirements,
    } = useReactiveVar(reactiveWorkspaceActions)
    // destructuring requirements to create a new pill, text, UOM
    const {
        textWithPill,
        insertPillIndex,
        variant,
        pillType,
        fieldState,
        template,
        setFieldState,
        changeType,
        element,
        event,
        value,
        saveChanges,
        setProperty,
        titleText,
    } = insertPillRequirements

    const isArticleFocused = React.useMemo(() => {
        return (
            workspaceTemplateData?.articleId ===
            workspaceTemplateData?.contentItem?.id
        )
    }, [workspaceTemplateData])

    React.useEffect(() => {
        if (isMoveUpAndDownTriggered && isShortcutKey) {
            handleMoveUpAndDown(action)
            setMoveUpAndDownTrigger({
                isMoveUpAndDownTriggered: false,
                action: '',
                isShortcutKey: false,
            })
        }
    }, [isMoveUpAndDownTriggered, setMoveUpAndDownTrigger])

    // useEffect to trigger the promote/demote button clicked from the workspace action bar
    const handleParagraphPromoteDemote = React.useCallback(
        (action: ParagraphAction) => {
            const promoteDemoteContent: PromoteDemoteRequirements = {
                content: promoteDemoteWorkspaceTemplateData,
                action,
            }
            setPromoteDemoteContent({ promoteDemoteContent })
            setPromoteDemoteTriggered({ isPromoteDemoteTriggered: true })
        },
        [promoteDemoteWorkspaceTemplateData, setPromoteDemoteContent]
    )

    // validate workspace action bar promote button if enabled or disabled
    const isPromoteEnabled = React.useMemo(() => {
        const isBaseContentPromoteDemoteAllowed =
            contentTeamFromStudioPage || isDraftMode
        return isContentAllowedToBePromoted(
            promoteDemoteWorkspaceTemplateData,
            isBaseContentPromoteDemoteAllowed!
        )
    }, [
        isContentAllowedToBePromoted,
        promoteDemoteWorkspaceTemplateData,
        contentTeamFromStudioPage,
        isDraftMode,
    ])

    // validate workspace action bar demote button if enabled or disabled
    const isDemoteEnabled = React.useMemo(() => {
        const isBaseContentPromoteDemoteAllowed =
            contentTeamFromStudioPage || isDraftMode
        return isContentAllowedToBeDemoted(
            promoteDemoteWorkspaceTemplateData,
            isBaseContentPromoteDemoteAllowed!
        )
    }, [
        isContentAllowedToBeDemoted,
        promoteDemoteWorkspaceTemplateData,
        contentTeamFromStudioPage,
        isDraftMode,
    ])

    // validate workspace action bar move up button if enabled or disabled
    const isMoveUpEnabled = React.useMemo(() => {
        if (
            workspaceTemplateData?.contentItem?.textType === 'Article' &&
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE' &&
            (!areSharedVisible || (isOMUser && contentTeamFromStudioPage))
        ) {
            return false
        }
        return (
            isInProject &&
            workspaceTemplateData &&
            !workspaceTemplateData.isFirstSibling &&
            // disable move up for PRs below table
            (!workspaceTemplateData.isTableSibling ||
                (workspaceTemplateData.isTableSibling &&
                    workspaceTemplateData.order! > 1))
        )
    }, [
        workspaceTemplateData,
        isInProject,
        selectedProjectElementNode,
        areSharedVisible,
    ])

    // validate workspace action bar move down button if enabled or disabled
    const isMoveDownEnabled = React.useMemo(() => {
        if (!workspaceTemplateData) return false

        if (
            workspaceTemplateData?.contentItem?.textType === 'Article' &&
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE' &&
            (!areSharedVisible || (isOMUser && contentTeamFromStudioPage))
        ) {
            return false
        }

        // If not in a project or if a topic is selected, ensure it's not the last item
        if (!isInProject || (isInProject && selectedTopic)) {
            return !isTemplateDataLastItem
        }

        // If an article is focused, make sure it's not the last article
        if (isInProject && isArticleFocused) {
            return !workspaceTemplateData?.isLastSibling
        }

        if (workspaceTemplateData?.isSustainabilityContentItem) {
            return !workspaceTemplateData?.isLastSibling
        }

        // If not in a project, simply check if it's not the last item
        return !isTemplateDataLastItem
    }, [
        isTemplateDataLastItem,
        isInProject,
        workspaceTemplateData,
        isArticleFocused,
        selectedTopic,
    ])

    /* This handles the paragraph reordering for the move up and move down functionality for the workspace action bar
           It will also validate if you are moving a paragraph from the product family or the product type*/
    const handleMoveUpAndDown = React.useCallback(
        (action: MoveAction) => {
            setMoveUpAndDownTrigger({
                isMoveUpAndDownTriggered: true,
                action,
                isShortcutKey: false,
            })
            const siblingDTV =
                dtv?.items
                    ?.filter(
                        (item) =>
                            item?.parentId === workspaceTemplateData?.parentId
                    )
                    ?.sort((a, b) => (a?.order || 0) - (b?.order || 0)) ?? []

            // if there is no sibling presents in DocumentTemplateViewItem , check sibling inside DocumentTemplateViewContentItem
            const isContentItem: boolean =
                workspaceTemplateData?.type === 'ContentItem' ||
                siblingDTV?.length === 0
            const innerContent =
                workspaceTemplateData?.documentTemplateViewItem?.documentTemplateViewContents?.[0]?.documentTemplateViewContentItems
                    ?.filter(
                        (item) =>
                            item?.parentId === workspaceTemplateData?.parentId
                    )
                    ?.sort((a, b) => (a?.order || 0) - (b?.order || 0)) ?? []
            const currentIndex =
                (isContentItem
                    ? innerContent
                    : siblingDTV
                )?.findIndex((item: any) =>
                    isContentItem
                        ? item?.id === workspaceTemplateData?.id
                        : item?.itemId === workspaceTemplateData?.itemId
                ) ?? 0

            const shouldConsiderPTGroupWhileMovingArticles =
                !isContentItem && !isSMMSpecificAction

            const actionCall =
                action === MoveDown ? getMoveDownIndex : getMoveUpIndex
            const reorderedSiblings = isSMMSpecificAction
                ? moveItemOrGroup(
                      isContentItem ? innerContent : siblingDTV ?? [],
                      currentIndex,
                      action
                  )
                : setPlacementForBulletsForDTV(
                      isContentItem ? innerContent : siblingDTV ?? [],
                      actionCall(
                          currentIndex,
                          siblingDTV,
                          shouldConsiderPTGroupWhileMovingArticles
                      )!,
                      currentIndex
                  )
            if (
                selectedProjectElementNode?.elementType === 'PRODUCTFAMILY' ||
                selectedTopic
            ) {
                handleFamilyReorderTemplates?.({
                    parentId: workspaceTemplateData?.parentId ?? '',
                    documentTemplateViewId:
                        workspaceTemplateData?.documentTemplateView?.id,
                    isContentItem: isContentItem,
                    projectProductFamilyId,
                    viewContentItemReorderInputs: reorderedSiblings?.map(
                        (each: any, index) => ({
                            contentItemId: isContentItem
                                ? each?.id
                                : each?.documentTemplateViewContents?.[0]
                                      ?.documentTemplateViewContentItems?.[0]
                                      ?.id,
                            order: each?.order,
                        })
                    ),
                })
            } else {
                handleProductTypeReorderTemplates?.({
                    parentId: workspaceTemplateData?.parentId ?? '',
                    documentTemplateViewId:
                        workspaceTemplateData?.documentTemplateView?.id,
                    isContentItem: isContentItem,
                    projectNuggetId: projectNuggetId,
                    viewContentItemReorderInputs: reorderedSiblings.map(
                        (each: any, index) => ({
                            contentItemId: isContentItem
                                ? each?.id
                                : each?.documentTemplateViewContents?.[0]
                                      ?.documentTemplateViewContentItems?.[0]
                                      ?.id,
                            order: each?.order,
                        })
                    ),
                })
            }
            setWorkspaceTemplateData(undefined)
            resetOnFocusProperty()
        },
        [
            workspaceTemplateData,
            contentItemNodes,
            dtv,
            selectedProjectElementNode,
            projectNuggetId,
            handleFamilyReorderTemplates,
            handleProductTypeReorderTemplates,
            selectedTopic,
        ]
    )

    const disableInsertPillTextUOMButtons = React.useCallback(
        () =>
            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                isInsertUOM: false,
                isAddSpecifierNote: false,
            }),
        [setReactiveWorkspaceActions]
    )

    const handleInsertPill = () => {
        if (isInsertPill) {
            try {
                setReactiveWorkspaceActions({
                    isInsertPill: false,
                    isInsertText: false,
                    isInsertUOM: true,
                    isAddSpecifierNote: false,
                    insertPillRequirements: {
                        ...insertPillRequirements,
                        variant: TextVariants.uomInPill,
                    },
                })

                insertPillTextUOM({
                    pillType: TextVariants.newPill,
                    insertPillIndex: insertPillIndex ?? 0,
                    changeType,
                    element,
                    event: event ?? undefined,
                    fieldState,
                    setFieldState,
                    value,
                    variant,
                    saveChanges,
                    setProperty,
                    template,
                    titleText,
                })
            } catch (error) {
                // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
                disableInsertPillTextUOMButtons()
            }
        }
    }

    const handleInsertText = () => {
        if (isInsertText) {
            try {
                disableInsertPillTextUOMButtons()

                insertPillTextUOM({
                    pillType: TextVariants.newReplacement,
                    insertPillIndex: insertPillIndex ?? 0,
                    changeType,
                    element,
                    event: event ?? undefined,
                    fieldState,
                    setFieldState,
                    value,
                    variant,
                    saveChanges,
                    setProperty,
                    template,
                    titleText,
                })
            } catch (error) {
                // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
                disableInsertPillTextUOMButtons()
            }
        }
    }

    const handleInsertUOM = () => {
        const isUOMVariant =
            areEq(variant, TextVariants.newPill) ||
            areEq(variant, TextVariants.uomInPill)

        try {
            if (isInsertUOM) {
                // variant is uomInPill/newPill? then, use "insertUomPill" handler
                if (insertUomPill && isUOMVariant) {
                    insertUomPill(insertPillIndex, textWithPill)
                } else {
                    // otherwise, use "insertPill" handler (implemented inside insertPillTextUOM method)
                    insertPillTextUOM({
                        pillType: TextVariants.newUOM,
                        insertPillIndex: insertPillIndex ?? 0,
                        changeType,
                        element,
                        event: event ?? undefined,
                        fieldState,
                        setFieldState,
                        value,
                        variant,
                        saveChanges,
                        setProperty,
                        template,
                        titleText,
                    })
                }
            }

            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                // enable "Insert Pill" button when UOM is inserted in pill
                isInsertUOM: isUOMVariant,
                isAddSpecifierNote: false,
                insertPillRequirements: {
                    ...insertPillRequirements,
                    // use to signify that the pill is created and can insert UOM in it, otherwise 'undefined'
                    variant: areEq(variant, TextVariants.uomInPill)
                        ? TextVariants.uomInPill
                        : undefined,
                },
            })
        } catch (error) {
            // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
            disableInsertPillTextUOMButtons()
        }
    }

    const handleAddSpecifierNote = () => {
        if (isAddSpecifierNote) {
            try {
                addNoteSpecifier?.(
                    addNoteSpecifierRequirements?.selectedTemplate
                )
            } catch (error) {
                // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
                // error message will be handle by global snackbar on 'addNoteSpecifier' handler
            } finally {
                disableInsertPillTextUOMButtons()
            }
        }
    }

    const noAction = () => {}
    const isSMMSpecificAction =
        contentTeamFromStudioPage && isDraftMode && showSMMActionButtons

    // Action Buttons Configuration
    // Add/Update button props as necessary for future enhancement requests.
    // Also, order of action button matters when rendering
    const EDITABLE_CONTENT_ACTION_BUTTONS: Partial<ActionButtons> = {
        MoveUp: {
            tooltip: {
                label: 'Move Up',
                smallLabel: ShortcutKeys.MoveUp,
            },
            variant: MoveUp,
            disabled: !isMoveUpEnabled,
            onClick: !!isMoveUpEnabled
                ? () => handleMoveUpAndDown(MoveUp)
                : noAction,
            svgIcon: (
                <ExtendedSvgIcon>
                    <path d="M3 21V19H21V21H3ZM11 17V10.825L9.4 12.4L8 11L12 7L16 11L14.6 12.4L13 10.825V17H11ZM3 5V3H21V5H3Z" />
                </ExtendedSvgIcon>
            ),
        },
        MoveDown: {
            tooltip: {
                label: 'Move Down',
                smallLabel: ShortcutKeys.MoveDown,
            },
            variant: MoveDown,
            disabled: !isMoveDownEnabled,
            onClick: !!isMoveDownEnabled
                ? () => handleMoveUpAndDown(MoveDown)
                : noAction,
            svgIcon: (
                <ExtendedSvgIcon>
                    <path d="M3 5V3H21V5H3ZM12 17L8 13L9.4 11.6L11 13.175V7H13V13.175L14.6 11.6L16 13L12 17ZM3 21V19H21V21H3Z" />
                </ExtendedSvgIcon>
            ),
        },
        Demote: {
            tooltip: {
                label: 'Demote',
                smallLabel: ShortcutKeys.Demote,
            },
            variant: Demote,
            disabled: !isDemoteEnabled,
            onClick: !!isDemoteEnabled
                ? () => handleParagraphPromoteDemote(Demote)
                : noAction,
            svgIcon: (
                <ExtendedSvgIcon>
                    <path d="M3 21V19H21V21H3ZM11 17V15H21V17H11ZM11 13V11H21V13H11ZM11 9V7H21V9H11ZM3 5V3H21V5H3ZM3 16V8L7 12L3 16Z" />
                </ExtendedSvgIcon>
            ),
        },
        Promote: {
            tooltip: {
                label: 'Promote',
                smallLabel: ShortcutKeys.Promote,
            },
            variant: Promote,
            disabled: !isPromoteEnabled,
            onClick: !!isPromoteEnabled
                ? () => handleParagraphPromoteDemote(Promote)
                : noAction,
            svgIcon: (
                <ExtendedSvgIcon>
                    <path d="M3 21V19H21V21H3ZM11 17V15H21V17H11ZM11 13V11H21V13H11ZM11 9V7H21V9H11ZM3 5V3H21V5H3ZM7 16L3 12L7 8V16Z" />
                </ExtendedSvgIcon>
            ),
        },
        Divider1: {
            variant: 'Divider',
        },
        InsertPill: {
            tooltip: {
                label: 'Insert Pill',
                smallLabel: ShortcutKeys.InsertPill,
            },
            variant: InsertPill,
            disabled: !isInsertPill,
            isHidden: !isSMMSpecificAction,
            onClick: isInsertPill ? handleInsertPill : noAction,
            svgIcon: (
                <ExtendedSvgIcon
                    style={{
                        fill: 'none',
                        stroke: '#ffffff',
                        strokeWidth: '2px',
                    }}
                >
                    <path d="M7,7h10c2.8,0,5,2.2,5,5l0,0c0,2.8-2.2,5-5,5H7c-2.8,0-5-2.2-5-5l0,0C2,9.2,4.2,7,7,7z" />
                </ExtendedSvgIcon>
            ),
        },
        InsertText: {
            tooltip: {
                label: 'Insert Text Field',
                smallLabel: ShortcutKeys.InsertText,
            },
            variant: InsertText,
            disabled: !isInsertText,
            isHidden: !isSMMSpecificAction,
            onClick: isInsertText ? handleInsertText : noAction,
            svgIcon: (
                <ExtendedSvgIcon>
                    <path d="M10.5,20V7H5V4h14v3h-5.5v13H10.5z" />
                </ExtendedSvgIcon>
            ),
        },
        InsertUOM: {
            tooltip: {
                label: 'Insert Unit of Measure',
                smallLabel: ShortcutKeys.InsertUOM,
            },
            variant: InsertUOM,
            disabled: !isInsertUOM,
            isHidden: !isSMMSpecificAction,
            onClick: isInsertUOM ? handleInsertUOM : noAction,
            svgIcon: (
                <ExtendedSvgIcon>
                    <path
                        d="M4,18c-0.5,0-1-0.2-1.4-0.6C2.2,17,2,16.5,2,16V8c0-0.6,0.2-1,0.6-1.4C3,6.2,3.5,6,4,6h16c0.5,0,1,0.2,1.4,0.6
    C21.8,7,22,7.4,22,8v8c0,0.5-0.2,1-0.6,1.4C21,17.8,20.5,18,20,18H4z M4,16h16V8h-3v4h-2V8h-2v4h-2V8H9v4H7V8H4V16z"
                    />
                </ExtendedSvgIcon>
            ),
        },
        Divider2: {
            variant: 'Divider',
        },
        SpecifierNote: {
            tooltip: {
                label: 'Add Specifier Note',
                smallLabel: ShortcutKeys.AddSpecifierNote,
            },
            variant: SpecifierNote,
            disabled: !isAddSpecifierNote,
            isHidden: !isSMMSpecificAction,
            onClick: isAddSpecifierNote ? handleAddSpecifierNote : noAction,
            svgIcon: (
                <ExtendedSvgIcon viewBox="-3 -4 28 28">
                    <path
                        d="M7,14c0.3,0,0.5-0.1,0.7-0.3S8,13.3,8,13s-0.1-0.5-0.3-0.7S7.3,12,7,12s-0.5,0.1-0.7,0.3S6,12.7,6,13s0.1,0.5,0.3,0.7
    S6.7,14,7,14z M7,11c0.3,0,0.5-0.1,0.7-0.3S8,10.3,8,10c0-0.3-0.1-0.5-0.3-0.7C7.5,9.1,7.3,9,7,9S6.5,9.1,6.3,9.3
    C6.1,9.5,6,9.7,6,10c0,0.3,0.1,0.5,0.3,0.7S6.7,11,7,11z M7,8c0.3,0,0.5-0.1,0.7-0.3S8,7.3,8,7S7.9,6.5,7.7,6.3S7.3,6,7,6
    S6.5,6.1,6.3,6.3S6,6.7,6,7s0.1,0.5,0.3,0.7S6.7,8,7,8z M10,14h5v-2h-5V14z M10,11h8V9h-8V11z M10,8h8V6h-8V8z M2,22V4
    c0-0.5,0.2-1,0.6-1.4S3.5,2,4,2h16c0.5,0,1,0.2,1.4,0.6C21.8,3,22,3.5,22,4v12c0,0.5-0.2,1-0.6,1.4C21,17.8,20.5,18,20,18H6L2,22z
     M5.2,16H20V4H4v13.1L5.2,16z"
                    />
                </ExtendedSvgIcon>
            ),
        },
    }

    // destructure computed properties of ActionButtons configuration from actionButtonsHelper
    const {
        getNonHidden,
        isConsecutiveDivider,
        isLastDivider,
        shouldHideActionBar,
    } = actionButtonsHelper(EDITABLE_CONTENT_ACTION_BUTTONS)

    return (
        <Typography
            component="div"
            className={`${roundContainerActionBar} ${
                shouldHideActionBar && contentTeamFromStudioPage
                    ? collapseActionBar
                    : expandActionBar
            }`}
        >
            <div className={actionButtonGroup}>
                {getNonHidden.map((props, idx) => {
                    // if no props OR last divider OR consecutive dividers, don't render
                    if (
                        !props ||
                        isLastDivider(idx) ||
                        isConsecutiveDivider(idx)
                    ) {
                        return null
                    }
                    return (
                        <ActionButton
                            key={`${props.variant}${idx}`}
                            {...props}
                        />
                    )
                })}
            </div>
        </Typography>
    )
}
